import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdminService } from '../../../service/admin.service';
import { StatsDefinitionDialogComponent, StatDefinition } from './dialogs/stats-definition-dialog/stats-definition-dialog.component';

export interface StatData {
  id: number;
  category: string;
  table_name: string;
  key_name: string;
  aggregation_type: 'tally' | 'average';
  value_min: number;
  value_max: number;
  created_at: string;
  updated_at: string;
  current_value: number;
  submission_count: number;
  last_updated: string;
}

export interface GroupedStats {
  [category: string]: {
    [table: string]: StatData[];
  };
}

@Component({
  selector: 'app-admin-stats',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatProgressBarModule,
    MatChipsModule,
    MatExpansionModule,
    MatTableModule,
    MatToolbarModule,
    MatMenuModule,
    MatTooltipModule
  ],
  templateUrl: './stats.component.html',
  styleUrl: './stats.component.less'
})
export class AdminStatsComponent implements OnInit {
  adminService = inject(AdminService);
  dialog = inject(MatDialog);
  snackBar = inject(MatSnackBar);

  stats: GroupedStats = {};
  isLoading = false;
  expandedCategories: Set<string> = new Set();

  ngOnInit() {
    this.loadStats();
  }

  loadStats() {
    this.isLoading = true;
    this.adminService.getStats().subscribe({
      next: (stats) => {
        this.stats = stats;
        this.isLoading = false;
      },
      error: (error) => {
        this.snackBar.open('Failed to load stats', 'Close', { duration: 3000 });
        this.isLoading = false;
        console.error('Error loading stats:', error);
      }
    });
  }

  onExpansionChange(category: string, event: any) {
    const isExpanded = event;
    if (isExpanded) {
      this.expandedCategories.add(category);
    } else {
      this.expandedCategories.delete(category);
    }
  }

  isCategoryExpanded(category: string): boolean {
    return this.expandedCategories.has(category);
  }

  startCreate() {
    const dialogData = {
      isEdit: false
    };

    const dialogRef = this.dialog.open(StatsDefinitionDialogComponent, {
      data: dialogData,
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveDefinition(result, 'create');
      }
    });
  }

  startCreateForCategory(category: string) {
    const dialogData = {
      isEdit: false,
      prefillCategory: category
    };

    const dialogRef = this.dialog.open(StatsDefinitionDialogComponent, {
      data: dialogData,
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveDefinition(result, 'create');
      }
    });
  }

  startCreateForTable(category: string, table: string) {
    const dialogData = {
      isEdit: false,
      prefillCategory: category,
      prefillTable: table
    };

    const dialogRef = this.dialog.open(StatsDefinitionDialogComponent, {
      data: dialogData,
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveDefinition(result, 'create');
      }
    });
  }

  startEdit(stat: StatData) {
    const dialogData = {
      isEdit: true,
      definition: {
        id: stat.id,
        category: stat.category,
        table_name: stat.table_name,
        key_name: stat.key_name,
        aggregation_type: stat.aggregation_type,
        value_min: stat.value_min,
        value_max: stat.value_max
      }
    };

    const dialogRef = this.dialog.open(StatsDefinitionDialogComponent, {
      data: dialogData,
      width: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.saveDefinition(result, 'update');
      }
    });
  }

  private saveDefinition(definition: StatDefinition, action: 'create' | 'update') {
    this.adminService.manageStatDefinition(action, definition).subscribe({
      next: (response) => {
        this.snackBar.open(
          action === 'update' ? 'Stat definition updated successfully' : 'Stat definition created successfully',
          'Close',
          { duration: 3000 }
        );
        this.loadStats();
      },
      error: (error) => {
        this.snackBar.open('Failed to save stat definition', 'Close', { duration: 3000 });
        console.error('Error saving stat definition:', error);
      }
    });
  }

  deleteDefinition(stat: StatData) {
    if (confirm(`Are you sure you want to delete "${stat.category} > ${stat.table_name} > ${stat.key_name}"? This will also delete all associated data.`)) {
      this.adminService.manageStatDefinition('delete', { id: stat.id }).subscribe({
        next: (response) => {
          this.snackBar.open('Stat definition deleted successfully', 'Close', { duration: 3000 });
          this.loadStats();
        },
        error: (error) => {
          this.snackBar.open('Failed to delete stat definition', 'Close', { duration: 3000 });
          console.error('Error deleting stat definition:', error);
        }
      });
    }
  }

  resetStatData(stat: StatData) {
    if (confirm(`Are you sure you want to reset "${stat.category} > ${stat.table_name} > ${stat.key_name}" data to 0?`)) {
      this.adminService.manageStatDefinition('reset', { id: stat.id }).subscribe({
        next: (response) => {
          this.snackBar.open('Stat data reset successfully', 'Close', { duration: 3000 });
          this.loadStats();
        },
        error: (error) => {
          this.snackBar.open('Failed to reset stat data', 'Close', { duration: 3000 });
          console.error('Error resetting stat data:', error);
        }
      });
    }
  }

  getProgressPercentage(stat: StatData): number {
    if (stat.value_max === stat.value_min) return 0;
    return Math.min(100, Math.max(0, ((stat.current_value - stat.value_min) / (stat.value_max - stat.value_min)) * 100));
  }

  getCategories(): string[] {
    return Object.keys(this.stats);
  }

  getTablesForCategory(category: string): string[] {
    return Object.keys(this.stats[category] || {});
  }

  getStatsForTable(category: string, table: string): StatData[] {
    return this.stats[category]?.[table] || [];
  }

  getTotalStatsCount(): number {
    let total = 0;
    for (const category of this.getCategories()) {
      for (const table of this.getTablesForCategory(category)) {
        total += this.getStatsForTable(category, table).length;
      }
    }
    return total;
  }
}
